import React, { useEffect, useRef } from 'react';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import buildImageUrl from '../../config/imageUrlHelper';

function getSocialLabel(rrss) {
  if (!rrss) return '';
  const url = rrss.toLowerCase();
  if (url.includes('twitch')) return 'Twitch';
  if (url.includes('tiktok')) return 'TikTok';
  if (url.includes('instagram')) return 'Instagram';
  if (url.includes('twitter') || url.includes('x.com')) return 'X / Twitter';
  if (url.includes('youtube') || url.includes('youtu.be')) return 'YouTube';
  if (url.includes('kick.com')) return 'Kick';
  if (url.includes('facebook') || url.includes('fb.com') || url.includes('fb.me')) return 'Facebook';
  return 'Red Social';
}

export default function ResultadoParticipantesModal({ score, participantes, onClose }) {
  const backdropRef = useRef(null);
  useLockBodyScroll();

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      ref={backdropRef}
      className="fixed inset-x-0 top-16 bottom-28 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:top-20 sm:bottom-24 sm:p-4"
      onClick={(e) => {
        if (e.target === backdropRef.current) {
          onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b dark:border-gray-700 gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-extrabold dark:text-white">Participantes con {score} aciertos</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Ordenados por comodines restantes (de mayor a menor)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4">
          {participantes.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No hay participantes con este resultado.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {participantes.map((p, idx) => {
                const content = (
                  <>
                    <div className="w-full text-center">
                      <span className="font-bold text-xs text-gray-400 dark:text-gray-500">#{idx + 1}</span>
                    </div>

                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 mx-auto">
                      {p.imagen ? (
                        <img
                          src={buildImageUrl(p.imagen)}
                          alt={p.nombre}
                          className="w-full h-full object-cover"
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = '/media/images/default-fallback.png';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-7 h-7 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="w-full text-center">
                      <p className="font-semibold text-xs sm:text-sm text-gray-800 dark:text-white truncate">{p.nombre}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                        {p.rrss ? `Perfil en ${getSocialLabel(p.rrss)}` : 'Sin red social vinculada'}
                      </p>
                    </div>

                    <div className="w-full flex items-center justify-center gap-2 text-center">
                      <div className="min-w-0">
                        <div className="text-xs sm:text-sm font-extrabold text-gray-800 dark:text-white leading-none">
                          {Number(p.preguntas_acertadas) || 0}
                        </div>
                        <div className="text-[9px] text-gray-400 leading-none">aciertos</div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs sm:text-sm font-bold text-emerald-600 dark:text-emerald-400 leading-none">
                          {Number(p.comodines_restantes) || 0}
                        </div>
                        <div className="text-[9px] text-gray-400 leading-none">comodines</div>
                      </div>
                    </div>
                  </>
                );

                if (p.rrss) {
                  return (
                    <a
                      key={p.id || `${p.nombre}-${idx}`}
                      href={p.rrss}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-2 p-2 sm:p-3 rounded-xl border bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/40 dark:hover:bg-blue-950/20 transition-colors min-h-[150px]"
                      title={`Ver perfil de ${p.nombre}`}
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <div
                    key={p.id || `${p.nombre}-${idx}`}
                    className="flex flex-col items-center gap-2 p-2 sm:p-3 rounded-xl border bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 min-h-[150px]"
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
